import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import { useTrackingStops, type TrackingStop, type StopUpsertPayload } from "hooks/useTrackingStops";
import { useTrackingRoutes } from "hooks/useTrackingRoutes";
import { showToast, toastTypes } from "utils";

export default function TrackingStopsPage() {
  const toast = useToast();
  const { stops, isLoading, error, create, update, remove } = useTrackingStops();
  const { routes } = useTrackingRoutes();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const cancelRef = useRef<any>(null);

  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<TrackingStop | null>(null);
  const [formValues, setFormValues] = useState<StopUpsertPayload>({ route_id: "", name: "", latitude: "", longitude: "", order: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<TrackingStop | null>(null);
  const [searchValue, setSearchValue] = useState("");

  const routeMap = useMemo(() => {
    const m: Record<string, string> = {};
    routes.forEach((r) => { m[r.id] = r.name; });
    return m;
  }, [routes]);

  const resetForm = useCallback(() => {
    setFormValues({ route_id: "", name: "", latitude: "", longitude: "", order: 0 });
    setEditing(null);
    setFormMode("create");
  }, []);

  const openCreate = useCallback(() => { resetForm(); setFormMode("create"); onOpen(); }, [onOpen, resetForm]);
  const openEdit = useCallback((row: TrackingStop) => {
    setEditing(row);
    setFormMode("edit");
    setFormValues({
      route_id: row.route_id,
      name: row.name,
      latitude: row.latitude,
      longitude: row.longitude,
      order: row.order,
    });
    onOpen();
  }, [onOpen]);

  const handleSubmit = useCallback(async () => {
    if (!formValues.route_id || !formValues.name.trim()) {
      showToast({ toast, message: "Route et nom obligatoires.", status: toastTypes.error });
      return;
    }
    const lat = typeof formValues.latitude === "string" ? parseFloat(formValues.latitude) : formValues.latitude;
    const lng = typeof formValues.longitude === "string" ? parseFloat(formValues.longitude) : formValues.longitude;
    if (isNaN(lat) || isNaN(lng)) {
      showToast({ toast, message: "Latitude et longitude valides obligatoires.", status: toastTypes.error });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: StopUpsertPayload = {
        route_id: formValues.route_id,
        name: formValues.name.trim(),
        latitude: lat,
        longitude: lng,
        order: formValues.order,
      };
      if (formMode === "create") {
        await create(payload);
        showToast({ toast, message: "Arrêt créé.", status: toastTypes.success });
      } else if (editing) {
        await update(editing.id, payload);
        showToast({ toast, message: "Arrêt mis à jour.", status: toastTypes.success });
      }
      onClose();
      resetForm();
    } catch (e: any) {
      showToast({ toast, message: e?.response?.data?.detail || e?.message || "Erreur.", status: toastTypes.error });
    } finally {
      setIsSubmitting(false);
    }
  }, [create, update, editing, formMode, formValues, onClose, resetForm, toast]);

  const handleDeleteClick = useCallback((row: TrackingStop) => { setActiveItem(row); onConfirmOpen(); }, [onConfirmOpen]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!activeItem) return;
    setDeletingId(activeItem.id);
    try {
      await remove(activeItem.id);
      showToast({ toast, message: "Arrêt supprimé.", status: toastTypes.success });
      onConfirmClose();
      setActiveItem(null);
    } catch (e: any) {
      showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error });
    } finally {
      setDeletingId(null);
    }
  }, [activeItem, remove, toast, onConfirmClose]);

  const filteredData = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return stops;
    return stops.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (routeMap[s.route_id] && routeMap[s.route_id].toLowerCase().includes(q))
    );
  }, [stops, searchValue, routeMap]);

  const columns = useMemo(() => {
    const col = createColumnHelper<TrackingStop>();
    return [
      col.accessor("name", { header: "Libellé", cell: (info) => <Text>{info.getValue()}</Text> }),
      col.accessor("route_id", { header: "Route", cell: (info) => <Text fontSize="sm">{routeMap[info.getValue()] ?? info.getValue()}</Text> }),
      col.accessor("order", { header: "Ordre", cell: (info) => <Text>{info.getValue()}</Text> }),
      col.accessor((r) => `${r.latitude} / ${r.longitude}`, { id: "coords", header: "Coordonnées", cell: (info) => <Text fontSize="sm">{info.getValue()}</Text> }),
      col.display({
        id: "action",
        header: "Action",
        cell: ({ row }) => (
          <HStack>
            <IconButton aria-label="Modifier" size="sm" icon={<EditIcon />} onClick={() => openEdit(row.original)} />
            <IconButton aria-label="Supprimer" size="sm" icon={<DeleteIcon />} colorScheme="red" onClick={() => handleDeleteClick(row.original)} isLoading={deletingId === row.original.id} />
          </HStack>
        ),
      }),
    ];
  }, [routeMap, openEdit, handleDeleteClick, deletingId]);

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" alignItems="start" px={{ base: "20px", sm: "24px" }} minHeight="100vh">
          <Flex justifyContent="space-between" alignItems="center" w="100%" flexWrap="wrap" gap={4}>
            <Heading size="text2xl" as="h5" fontFamily="Arimo">Arrêts</Heading>
            <Button size="xl" variant="outline" colorScheme="red_origin" onClick={openCreate}>Créer un arrêt</Button>
          </Flex>
          {error && <Box mt={4} p={4} bg="red.50" borderRadius="md"><Text color="red.700">Erreur chargement.</Text></Box>}
          <Box mt={4} bg="white" w="100%" borderRadius="10px">
            <Flex p={4}><InputGroup w={{ base: "100%", sm: "300px" }}>
              <Input placeholder="Rechercher" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
              <InputRightElement>{searchValue ? <IconButton aria-label="Effacer" size="sm" icon={<CloseIcon />} onClick={() => setSearchValue("")} /> : null}</InputRightElement>
            </InputGroup></Flex>
            {isLoading && !filteredData.length ? <Flex justify="center" py={10}><Spinner size="lg" /></Flex> : <ReactTable columns={columns as any} data={filteredData} size="sm" />}
            {!isLoading && !filteredData.length && <Box p={8} textAlign="center"><Text color="gray.600">Aucun arrêt.</Text></Box>}
          </Box>
        </Flex>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{formMode === "create" ? "Ajouter un arrêt" : "Modifier l'arrêt"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl isRequired>
                <FormLabel>Route</FormLabel>
                <Select
                  value={formValues.route_id}
                  onChange={(e) => setFormValues((p) => ({ ...p, route_id: e.target.value }))}
                  placeholder="Sélectionner une route"
                >
                  {routes.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired><FormLabel>Nom</FormLabel><Input value={formValues.name} onChange={(e) => setFormValues((p) => ({ ...p, name: e.target.value }))} /></FormControl>
              <FormControl isRequired><FormLabel>Latitude</FormLabel><Input type="number" step="any" value={formValues.latitude} onChange={(e) => setFormValues((p) => ({ ...p, latitude: e.target.value }))} /></FormControl>
              <FormControl isRequired><FormLabel>Longitude</FormLabel><Input type="number" step="any" value={formValues.longitude} onChange={(e) => setFormValues((p) => ({ ...p, longitude: e.target.value }))} /></FormControl>
              <FormControl isRequired><FormLabel>Ordre</FormLabel><Input type="number" value={formValues.order} onChange={(e) => setFormValues((p) => ({ ...p, order: parseInt(e.target.value, 10) || 0 }))} /></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting}>Enregistrer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Supprimer l&apos;arrêt</AlertDialogHeader>
            <AlertDialogBody>Êtes-vous sûr de vouloir supprimer cet arrêt ?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>Annuler</Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={deletingId === activeItem?.id} ml={3}>Supprimer</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
